import { useQuery } from '@tanstack/react-query';
import { axiosInstance } from '@/utils/lib/axios';
import { env } from '@/utils/constants/env';
import { errorHandler } from '@/utils/functions/errorHandler';
import { IChecklistLogsResponse, IRecentLogsResponse } from '@/types/checklist.types';

interface UseGetInstanceLogsParams {
  tenantId: string | number | undefined;
  instanceId: number | undefined;
  page?: number;
  pageSize?: number;
  order?: 'asc' | 'desc';
  orderBy?: string;
  search?: string;
  action?: string;
}

export const useGetInstanceLogs = ({
  tenantId,
  instanceId,
  page = 1,
  pageSize = 20,
  order,
  orderBy,
  search,
  action,
}: UseGetInstanceLogsParams) => {
  return useQuery({
    queryKey: ['checklist-logs', 'instance', tenantId, instanceId, page, pageSize, order, orderBy, search, action],
    queryFn: async () => {
      if (!tenantId || !instanceId) {
        throw new Error('Tenant ID and Instance ID are required');
      }

      try {
        const { data } = await axiosInstance.get<IChecklistLogsResponse>(
          `${env.apiTasklistBaseUrl}/api/v1/compro/${tenantId}/checklist-instances/${instanceId}/logs`,
          {
            params: { page, page_size: pageSize, order, orderBy, search, action },
          },
        );
        return data.data;
      } catch (error) {
        errorHandler(error);
        throw error;
      }
    },
    enabled: !!tenantId && !!instanceId,
    staleTime: 0,
    cacheTime: 0,
  });
};

interface UseGetRecentLogsParams {
  tenantId: string | number | undefined;
  instanceId: number | undefined;
  limit?: number;
}

export const useGetRecentLogs = ({ tenantId, instanceId, limit = 10 }: UseGetRecentLogsParams) => {
  return useQuery({
    queryKey: ['checklist-logs', 'recent', tenantId, instanceId, limit],
    queryFn: async () => {
      if (!tenantId || !instanceId) {
        throw new Error('Tenant ID and Instance ID are required');
      }

      try {
        const { data } = await axiosInstance.get<IRecentLogsResponse>(
          `${env.apiTasklistBaseUrl}/api/v1/compro/${tenantId}/checklist-instances/${instanceId}/logs/recent`,
          {
            params: { limit },
          },
        );
        return data.data.logs;
      } catch (error) {
        errorHandler(error);
        throw error;
      }
    },
    enabled: !!tenantId && !!instanceId,
    staleTime: 0,
    cacheTime: 0,
  });
};

interface UseGetLogsByActionParams {
  tenantId: string | number | undefined;
  instanceId: number | undefined;
  action: string | undefined;
  page?: number;
  pageSize?: number;
}

export const useGetLogsByAction = ({
  tenantId,
  instanceId,
  action,
  page = 1,
  pageSize = 20,
}: UseGetLogsByActionParams) => {
  return useQuery({
    queryKey: ['checklist-logs', 'action', tenantId, instanceId, action, page, pageSize],
    queryFn: async () => {
      if (!tenantId || !instanceId || !action) {
        throw new Error('Tenant ID, Instance ID, and Action are required');
      }

      try {
        const { data } = await axiosInstance.get<IChecklistLogsResponse>(
          `${env.apiTasklistBaseUrl}/api/v1/compro/${tenantId}/checklist-instances/${instanceId}/logs/actions/${action}`,
          {
            params: { page, page_size: pageSize },
          },
        );
        return data.data;
      } catch (error) {
        errorHandler(error);
        throw error;
      }
    },
    enabled: !!tenantId && !!instanceId && !!action,
    staleTime: 0,
    cacheTime: 0,
  });
};

interface UseGetResponseLogsParams {
  tenantId: string | number | undefined;
  responseId: number | undefined;
  page?: number;
  pageSize?: number;
}

export const useGetResponseLogs = ({ tenantId, responseId, page = 1, pageSize = 20 }: UseGetResponseLogsParams) => {
  return useQuery({
    queryKey: ['checklist-logs', 'response', tenantId, responseId, page, pageSize],
    queryFn: async () => {
      if (!tenantId || !responseId) {
        throw new Error('Tenant ID and Response ID are required');
      }

      try {
        const { data } = await axiosInstance.get<IChecklistLogsResponse>(
          `${env.apiTasklistBaseUrl}/api/v1/compro/${tenantId}/checklist-responses/${responseId}/logs`,
          {
            params: { page, page_size: pageSize },
          },
        );
        return data.data;
      } catch (error) {
        errorHandler(error);
        throw error;
      }
    },
    enabled: !!tenantId && !!responseId,
    staleTime: 0,
    cacheTime: 0,
  });
};
